/**
 * cexpand.js v2
 * Inline swatch expand/collapse — viewport-aware, no dependencies.
 *
 * Behavior:
 *   - Triggers WITH data-max-mobile (PDP-style):
 *       desktop default → expanded ("View Less", all items visible)
 *       mobile default  → collapsed ("View More", first N items visible)
 *   - Triggers WITHOUT data-max-mobile (collection-style):
 *       both viewports follow server-rendered aria-expanded
 *
 * Trigger attributes:
 *   data-target      — id of the container holding [data-index] items
 *   data-max         — items with index >= max are toggled (used on desktop)
 *   data-max-mobile  — optional. Used when viewport <= 749px. Presence also
 *                      signals "PDP-style behavior" (mobile default collapsed).
 *   data-more-text   — label when collapsed
 *   data-less-text   — label when expanded
 *   aria-expanded    — "true" | "false" (server-rendered initial state)
 *
 * Container side-effects:
 *   - If the container has class "cexpand--js-uninitialized", it is removed
 *     after init so any FOUC-mitigation CSS scoped to that class deactivates.
 *
 * Click side-effect:
 *   - Trigger gets data-user-toggled="true" on first click. After that,
 *     viewport changes will preserve the user's state instead of reverting
 *     to the per-viewport default.
 */

(function () {
  'use strict';

  var MOBILE_BREAKPOINT_PX = 749;
  var mql = window.matchMedia('(max-width: ' + MOBILE_BREAKPOINT_PX + 'px)');

  function isMobile() {
    return mql.matches;
  }

  function hasMobileMax(trigger) {
    var v = parseInt(trigger.getAttribute('data-max-mobile'), 10);
    return !isNaN(v);
  }

  function getMax(trigger) {
    var mobileMax = parseInt(trigger.getAttribute('data-max-mobile'), 10);
    var desktopMax = parseInt(trigger.getAttribute('data-max'), 10);
    if (isMobile() && !isNaN(mobileMax)) return mobileMax;
    return desktopMax;
  }

  function getContainer(trigger) {
    var id = trigger.getAttribute('data-target');
    return id ? document.getElementById(id) : null;
  }

  function applyVisibility(container, max, expanded) {
    var items = container.querySelectorAll('[data-index]');
    for (var i = 0; i < items.length; i++) {
      var idx = parseInt(items[i].getAttribute('data-index'), 10);
      if (idx >= max && !expanded) {
        items[i].classList.add('cexpand__hidden');
      } else {
        // Items below threshold OR when expanded: clear the hidden class.
        // The "always clear" branch ensures correct state after viewport changes.
        items[i].classList.remove('cexpand__hidden');
      }
    }
  }

  function updateLabel(trigger, isExpanded) {
    var moreText = trigger.getAttribute('data-more-text');
    if (!moreText) return;
    trigger.textContent = isExpanded
      ? trigger.getAttribute('data-less-text')
      : moreText;
  }

  function setState(trigger, expanded) {
    var container = getContainer(trigger);
    if (!container) return;
    applyVisibility(container, getMax(trigger), expanded);
    trigger.setAttribute('aria-expanded', String(expanded));
    updateLabel(trigger, expanded);
  }

  function handleClick(e) {
    var trigger = e.target.closest('.js-cexpand');
    if (!trigger) return;
    if (trigger.tagName === 'A') e.preventDefault();
    trigger.setAttribute('data-user-toggled', 'true');
    var wasExpanded = trigger.getAttribute('aria-expanded') === 'true';
    setState(trigger, !wasExpanded);
  }

  function init() {
    var triggers = document.querySelectorAll('.js-cexpand');
    for (var i = 0; i < triggers.length; i++) {
      var trigger = triggers[i];
      var serverExpanded = trigger.getAttribute('aria-expanded') === 'true';

      if (hasMobileMax(trigger) && isMobile()) {
        // PDP-style on mobile: force collapsed.
        setState(trigger, false);
      } else {
        // Collection or desktop PDP: keep server-rendered state, but re-apply
        // visibility so it's consistent with the current viewport's max.
        setState(trigger, serverExpanded);
      }

      var container = getContainer(trigger);
      if (container) container.classList.remove('cexpand--js-uninitialized');
    }
  }

  function handleViewportChange() {
    var triggers = document.querySelectorAll('.js-cexpand');
    for (var i = 0; i < triggers.length; i++) {
      var trigger = triggers[i];
      var userToggled = trigger.hasAttribute('data-user-toggled');

      if (hasMobileMax(trigger) && !userToggled) {
        // Auto-state per viewport: mobile=collapsed, desktop=expanded.
        setState(trigger, !isMobile());
      } else {
        // User has chosen explicit state or no mobile config — keep current
        // aria-expanded, just re-clamp visible items to the new viewport's max.
        var currentExpanded = trigger.getAttribute('aria-expanded') === 'true';
        setState(trigger, currentExpanded);
      }
    }
  }

  if (mql.addEventListener) {
    mql.addEventListener('change', handleViewportChange);
  } else if (mql.addListener) {
    mql.addListener(handleViewportChange);
  }

  document.addEventListener('click', handleClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());